import React, { useCallback, useEffect, useMemo, useState } from 'react';
import vtkCoordinate from '@kitware/vtk.js/Rendering/Core/Coordinate';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { configStore } from 'config/configStore';

function getDisplayValueSafe(
  renderer: vtkRenderer,
  position: [number, number, number],
) {
  try {
    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();
    coord.setValue(position);
    return coord.getComputedDisplayValue(renderer);
  } catch {
    return undefined;
  }
}

export interface TDataSource {
  id: number;
  name: string;
  position: [number, number, number];
  textAnchor?: 'start' | 'middle' | 'end';
  deltaX?: number;
  deltaY?: number;
  type?: string;
  isVisible?: boolean;
}

export default function SvgTextRenderer({
  dataSource,
  containerRef,
  svgRef,
  renderer,
  onCameraRotate,
  onDelete,
}: {
  dataSource: TDataSource[];
  containerRef: React.RefObject<HTMLDivElement>;
  svgRef: React.RefObject<SVGSVGElement>;
  renderer: vtkRenderer;
  onCameraRotate: () => void;
  onDelete?: (id: number) => void;
}) {
  if (!containerRef?.current) return null;
  const { analysisType } = usePanelContainer();
  const isFFR = analysisType === 'FFR';
  const TEXT_CHAR_WIDTH = 20;
  const FFR_TEXT_FONT_SIZE = 30;
  const FFR_SCALE = FFR_TEXT_FONT_SIZE / 40;
  const scaleFfr = (value: number) => Math.round(value * FFR_SCALE);
  const FFR_LABEL_LAYOUT = {
    lineShortX: scaleFfr(20),
    lineLongX: scaleFfr(50),
    lineShortY: -scaleFfr(20),
    lineLongY: -scaleFfr(70),
    boxOffsetX: scaleFfr(20),
    boxOffsetY: -scaleFfr(70),
    boxHeight: scaleFfr(50),
    boxRadius: Math.max(4, scaleFfr(8)),
    boxWidthPadding: scaleFfr(30),
    textOffsetX: scaleFfr(35),
    textOffsetY: -scaleFfr(45),
    textFontSize: FFR_TEXT_FONT_SIZE,
    textCharWidth: Math.max(8, scaleFfr(TEXT_CHAR_WIDTH)),
  };
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [deletedIndex, setDeletedIndex] = useState<number | null>(null);
  const [offsets, setOffsets] = useState<
    Record<number, { dx: number; dy: number }>
  >({});
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragIndex: -1,
  });

  const dims = containerRef.current.getBoundingClientRect();
  const positions = useMemo(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return [];
    const dpr = window.devicePixelRatio || 1;
    return dataSource.flatMap((data) => {
      const displayValue = getDisplayValueSafe(renderer, data.position);
      if (!displayValue) return [];
      const [x, y] = displayValue;
      return {
        id: data.id,
        startX: x / dpr,
        startY: (rect.height * dpr - y) / dpr,
      };
    });
  }, [dataSource, renderer, containerRef]);

  useEffect(() => {
    setOffsets((prev) => {
      const next = { ...prev };
      const ids = new Set<number>();
      dataSource.forEach((item) => {
        ids.add(item.id);
        if (!next[item.id]) {
          next[item.id] = {
            dx: item.deltaX ?? 0,
            dy: item.deltaY ?? 0,
          };
        }
      });
      Object.keys(next).forEach((key) => {
        const id = Number(key);
        if (!ids.has(id)) delete next[id];
      });
      return next;
    });
  }, [dataSource]);

  const currentColor = useCallback(
    (index: number) =>
      deletedIndex === index
        ? '#FF3333'
        : activeIndex === index
          ? '#1DD200'
          : hoverIndex === index
            ? '#FFDB20'
            : 'white',
    [activeIndex, deletedIndex, hoverIndex],
  );

  const handleMouseDown = (index: number) => {
    setActiveIndex(index);
    setDragState({ isDragging: true, dragIndex: index });
    if (containerRef.current) {
      containerRef.current.style.cursor = 'none';
    }
  };

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (
        dragState.isDragging &&
        dragState.dragIndex !== -1 &&
        containerRef.current
      ) {
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        const position = positions[dragState.dragIndex];
        const item = dataSource[dragState.dragIndex];
        if (!position || !item) return;

        const textTemp = item.name ?? '';
        const textWidth = textTemp.length * (isFFR ? FFR_LABEL_LAYOUT.textCharWidth : TEXT_CHAR_WIDTH);
        const baseX = position.startX + (isFFR ? textWidth / 2 : 0);
        const baseY = position.startY + (isFFR ? -20 : 0);

        setOffsets((prev) => ({
          ...prev,
          [item.id]: {
            dx: mouseX - baseX,
            dy: mouseY - baseY,
          },
        }));
        setHoverIndex(dragState.dragIndex);
        return;
      }

      const isLeftMouseDown = (event.buttons & 1) === 1;
      if (!isLeftMouseDown) return;
      onCameraRotate();
    };

    const handleMouseUp = () => {
      setDragState({ isDragging: false, dragIndex: -1 });
      setHoverIndex(null);
      setActiveIndex(null);
      if (containerRef.current) {
        //containerRef.current.style.cursor = 'default';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    dragState,
    positions,
    dataSource,
    containerRef,
    analysisType,
    onCameraRotate,
  ]);

  function getFontSizeFromWidth(): number {
    const scaleRatio = 0.025; // container 너비의 3.5%
    const minFontSize = 10;
    const maxFontSize = 20;
    const scaled = dims.width * scaleRatio;
    return Math.max(minFontSize, Math.min(maxFontSize, scaled));
  }

  return (
    <svg
      id="text-svg"
      ref={svgRef}
      width={dims.width}
      height={dims.height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {positions.map((pos, index) => {
        if (!pos) return null;
        const { startX, startY } = pos;
        if (Number.isNaN(startX) || Number.isNaN(startY)) return null;

        const {
          name: textTemp,
          textAnchor,
          isVisible,
        } = dataSource[index] ?? {
          name: '',
        };
        const isHidden =
          isFFR &&
          (configStore.get().isSvgCapture || isVisible) === false;

        if (isHidden) return null;
        const textWidth = textTemp.length * (isFFR ? FFR_LABEL_LAYOUT.textCharWidth : TEXT_CHAR_WIDTH);
        const baseOffset = offsets[pos.id];
        const deltaX = baseOffset?.dx ?? 0;
        const deltaY = baseOffset?.dy ?? 0;
        const endX = startX + deltaX;
        const endY = startY + deltaY;

        const anchorBaseX = startX + deltaX;
        const labelY = startY + (isFFR ? FFR_LABEL_LAYOUT.lineShortY : 0) + deltaY;
        const labelX = anchorBaseX + (isFFR ? textWidth / 2 : 0);
        const ffrBoxX = endX + FFR_LABEL_LAYOUT.boxOffsetX;
        const ffrBoxY = endY + FFR_LABEL_LAYOUT.boxOffsetY;
        const ffrBoxWidth = FFR_LABEL_LAYOUT.boxWidthPadding + textWidth;
        const ffrBoxHeight = FFR_LABEL_LAYOUT.boxHeight;
        const isGraphPointLabel = pos.id === -1;
        const ffrTextColor = isGraphPointLabel ? '#1DD200' : currentColor(index);

        const lineEndX = endX + FFR_LABEL_LAYOUT.lineLongX + textWidth;
        const lineEndYLong = endY + FFR_LABEL_LAYOUT.lineLongY;
        const lineEndYShort = endY + FFR_LABEL_LAYOUT.lineShortY;
        return (
          <g
            key={pos.id}
            onMouseDown={
              isFFR
                ? (e) => {
                    if (e.button !== 0) return;
                    e.stopPropagation();
                    const target = e.target as Element | null;
                    if (target?.closest?.('[data-role="delete"]')) return;
                    if (hoverIndex !== null) handleMouseDown(index);
                  }
                : undefined
            }
            style={{
              cursor: isFFR ? 'pointer' : 'default',
              pointerEvents: isFFR ? 'auto' : 'none',
            }}
            onMouseEnter={isFFR ? () => setHoverIndex(index) : undefined}
            onMouseOver={isFFR ? () => setHoverIndex(index) : undefined}
            onMouseLeave={
              isFFR
                ? () => {
                    setHoverIndex(null);
                    setDeletedIndex(null);
                  }
                : undefined
            }
          >
            {isFFR ? (
              <>
                <line
                  x1={startX}
                  y1={startY}
                  x2={
                    startX > lineEndX
                      ? lineEndX
                      : endX + FFR_LABEL_LAYOUT.lineShortX
                  }
                  y2={startY < lineEndYLong ? lineEndYLong : lineEndYShort}
                  stroke={currentColor(index)}
                  strokeOpacity={0.5}
                  strokeWidth="2"
                />

                {/* 박스 */}
                <rect
                  x={ffrBoxX}
                  y={ffrBoxY}
                  width={ffrBoxWidth}
                  height={ffrBoxHeight}
                  rx={0}
                  ry={0}
                  fill="rgba(0, 0, 0, 0.5)"
                  stroke={isGraphPointLabel ? '#1DD200' : 'none'}
                  strokeWidth={isGraphPointLabel ? 2 : 0}
                />

                {/* 텍스트 */}
                <text
                  x={endX + textWidth / 2 + FFR_LABEL_LAYOUT.textOffsetX}
                  y={endY + FFR_LABEL_LAYOUT.textOffsetY}
                  fontSize={FFR_LABEL_LAYOUT.textFontSize}
                  fontFamily="Pretendard"
                  fill={ffrTextColor}
                  fillOpacity="1"
                  textAnchor="middle"
                  alignmentBaseline="middle"
                >
                  {textTemp}
                </text>
              </>
            ) : (
              <text
                x={labelX}
                y={labelY}
                fontSize={getFontSizeFromWidth()}
                fontFamily="Pretendard"
                fill={'white'}
                textAnchor={textAnchor ?? 'middle'}
                fontWeight="bold"
                alignmentBaseline="middle"
              >
                {textTemp}
              </text>
            )}

            {hoverIndex === index &&
              (() => {
                const deleteX = isFFR
                  ? ffrBoxX + ffrBoxWidth - 8
                  : textAnchor === 'start'
                    ? anchorBaseX + textWidth + 25
                    : textAnchor === 'end'
                      ? anchorBaseX + 25
                      : anchorBaseX + textWidth / 2 + 25;
                const deleteY = isFFR ? ffrBoxY - 8 : labelY - 8;
                return (
                  <g
                    data-role="delete"
                    transform={`translate(${deleteX} ${deleteY})`}
                    pointerEvents="all"
                    style={{
                      cursor: 'pointer',
                    }}
                    onMouseOver={(e) => {
                      e.stopPropagation();
                      setDeletedIndex(index);
                    }}
                    onMouseLeave={() => setDeletedIndex(null)}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      onDelete?.(pos.id);
                      setDeletedIndex(null);
                      setHoverIndex(null);
                    }}
                  >
                    <rect
                      x={-6}
                      y={-6}
                      width={28}
                      height={28}
                      fill="transparent"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={16}
                      height={16}
                      fill={currentColor(index)}
                      viewBox="0 0 512 512"
                    >
                      <path
                        className="fa-secondary"
                        d="M0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zm168.1 61.7c.1-.7 .2-1.5 .3-2.3c.3-1.5 .7-3 1.3-4.4c1.2-2.9 2.9-5.6 5.3-7.9c15.7-15.7 31.4-31.4 47-47c-15.7-15.7-31.3-31.3-47-47c-9.4-9.4-9.4-24.6 0-33.9c4.7-4.7 10.8-7 17-7s12.3 2.3 17 7c15.7 15.7 31.4 31.4 47 47c15.7-15.7 31.4-31.4 47-47c4.7-4.7 10.8-7 17-7s12.3 2.3 17 7c9.4 9.4 9.4 24.6 0 33.9c-15.7 15.7-31.3 31.3-47 47c15.7 15.7 31.4 31.4 47 47c4.7 4.7 7 10.8 7 17s-2.3 12.3-7 17s-10.8 7-17 7s-12.3-2.3-17-7l-47-47c-15.7 15.7-31.3 31.3-47 47c-4.7 4.7-10.8 7-17 7s-12.3-2.3-17-7c-2.3-2.3-4.1-5.1-5.3-7.9c-.6-1.4-1-2.9-1.3-4.4c-.2-1.1-.4-2.3-.3-2.2c-.1-1.2-.1-1.2-.1-2.4c0-1.5 .1-1.9 .1-2.3z"
                      />
                      <path
                        className="fa-primary"
                        opacity=".4"
                        d="M209 175c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47z"
                      />
                    </svg>
                  </g>
                );
              })()}
          </g>
        );
      })}
    </svg>
  );
}
